import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Autocomplete, CircularProgress, debounce } from "@mui/material";
import { red } from "@mui/material/colors";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { routeConfigs } from "../../navigation/routeConfig";
import { UserContext } from "../../hooks/userContext";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";
import { useMutationCreateWantedItem } from "../../queries/wantedItem/hooks/useMutationCreateWantedItem";

const normalizeNdcValue = (value: string) => value.replace(/-/g, "");
const checkIfNdcSearch = (value: string): boolean => /^\d{3}/.test(value);

const capitalizeWords = (str: string) => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const NewWishlistItem: React.FunctionComponent = () => {
  const { id }: any = useContext(UserContext);
  const { addToast } = useContext(ToasterContext);
  const navigate = useNavigate();

  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const [fdaLoading, setFdaLoading] = useState<boolean>(false);
  const [fdaError, setFdaError] = useState<string>("");
  const [options, setOptions] = useState<any[]>([]);
  const [drugData, setDrugData] = useState<any>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const medispanAbortControllerRef = useRef<AbortController | null>(null);

  const [createWantedItem, { loading }] = useMutationCreateWantedItem({
    onCompleted: () => {
      addToast({ type: NotificationType.Success, title: "Wishlist item added." });
      navigate(routeConfigs.wishlist.path);
    },
    onError: (error: any) => {
      addToast({ type: NotificationType.Error, title: error.message });
    },
  });

  const handleMedispanSearch = async (value: string) => {
    if (medispanAbortControllerRef.current) {
      medispanAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    medispanAbortControllerRef.current = abortController;

    try {
      setFdaLoading(true);
      const api = `${process.env.REACT_APP_API_URL}/medispan/get?searchTerm=${encodeURIComponent(value)}`;
      const response = await axios.get(api, { signal: abortController.signal });
      setFdaLoading(false);

      if (!Array.isArray(response.data)) {
        setFdaError("No Medispan results found for this NDC.");
        return;
      }

      const filteredData = response.data
        .filter((item: any) => item.product_ndc !== null || item.package_ndc !== null)
        .filter((item: any) => {
          const usingNdc = checkIfNdcSearch(value);
          if (usingNdc) {
            const searchNdc = normalizeNdcValue(value);
            const productNdc = item.product_ndc ? normalizeNdcValue(item.product_ndc) : null;
            const packageNdc = item.package_ndc ? normalizeNdcValue(item.package_ndc) : null;
            return (
              (productNdc && productNdc.startsWith(searchNdc)) ||
              (packageNdc && packageNdc.startsWith(searchNdc))
            );
          }

          return true;
        })
        .map((item: any) => ({
          ...item,
          brand_name: item.brand_name ? capitalizeWords(item.brand_name) : item.brand_name,
          generic_name: item.generic_name ? capitalizeWords(item.generic_name) : item.generic_name,
        }));

      if (filteredData.length === 0) {
        setFdaError("No Medispan results found for this NDC.");
      } else {
        setFdaError("");
        setOptions(filteredData);
      }
    } catch (error: any) {
      if (error?.code === "ERR_CANCELED") return;
      setFdaLoading(false);
      addToast({
        type: NotificationType.Error,
        title: error.response ? error.message : "Server side error",
      });
    }
  };

  const debouncedMedispanSearch = useCallback(
    debounce((value: string) => {
      handleMedispanSearch(value);
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      if (medispanAbortControllerRef.current) {
        medispanAbortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSearch = (value: string) => {
    const trimmedValue = value.trimStart();
    setOptions([]);
    setFdaError("");
    setDrugData(null);

    if (trimmedValue.length >= 3) {
      debouncedMedispanSearch(trimmedValue);
    } else {
      setFdaLoading(false);
    }
  };

  const mappedValues = useMemo(() => {
    const ndcNumber = drugData?.package_ndc || drugData?.product_ndc || "";
    const drugName = drugData?.brand_name || drugData?.generic_name || "";
    const manufacturer = drugData?.labeler_name || "";
    const dosageForm = drugData?.dosage_form || "N/A";
    const strength = drugData?.active_ingredients?.[0]?.strength || "N/A";

    return { ndcNumber, drugName, manufacturer, dosageForm, strength };
  }, [drugData]);

  useEffect(() => {
    let isMounted = true;

    const resolveImage = async () => {
      if (!drugData) {
        setImageUrl(null);
        return;
      }

      const medispanImageUrl =
        drugData?.image_url || drugData?.imageUrl || drugData?.image?.url || null;
      if (medispanImageUrl) {
        setImageUrl(medispanImageUrl);
        return;
      }

      const ndcSource = drugData?.package_ndc || drugData?.product_ndc;
      if (!ndcSource) {
        setImageUrl(null);
        return;
      }

      try {
        const formattedNdc = ndcSource.replace(/-/g, "");
        const imageResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/drug-images?ndc=${formattedNdc}`
        );
        if (isMounted) {
          setImageUrl(imageResponse?.data?.imageUrl || null);
        }
      } catch (_error) {
        if (isMounted) {
          setImageUrl(null);
        }
      }
    };

    resolveImage();

    return () => {
      isMounted = false;
    };
  }, [drugData]);

  const handleSubmit = () => {
    if (!mappedValues.ndcNumber || !mappedValues.drugName || !mappedValues.manufacturer) {
      addToast({
        type: NotificationType.Warning,
        title: "Select an item from Medispan search first.",
      });
      return;
    }

    createWantedItem({
      variables: {
        data: {
          buyerUserId: +id,
          ndcNumber: mappedValues.ndcNumber,
          drugName: mappedValues.drugName,
          manufacturer: mappedValues.manufacturer,
          dosageForm: mappedValues.dosageForm,
          strength: mappedValues.strength,
        },
      },
    });
  };

  return (
    <Paper sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2 }}>
        <Typography component="h1" variant="h5">
          Add Wishlist Item
        </Typography>
      </Box>

      <Grid container sx={{ p: 2 }} spacing={3}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={options}
            filterOptions={(option: any) => option}
            inputValue={searchInputValue}
            getOptionLabel={(option: any) => {
              if (typeof option === "string") return option;
              if (!option) return "";
              return `${option?.brand_name || option?.generic_name} - ${option.package_ndc || option.product_ndc}`;
            }}
            isOptionEqualToValue={(option, value) => option?.package_ndc === value?.package_ndc}
            value={drugData}
            onChange={(_, newValue: any) => {
              setDrugData(newValue || null);
            }}
            onInputChange={(event: any, newValue: string) => {
              const normalizedValue = newValue.trimStart();
              setSearchInputValue(normalizedValue);
              if (event?.type === "change") {
                handleSearch(normalizedValue);
              }
            }}
            renderInput={(params) => (
              <>
                <TextField
                  {...params}
                  label="Drug name or NDC number"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {fdaLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
                {fdaError !== "" && (
                  <Box sx={{ fontSize: "12px", mt: 1, color: red[500] }}>{fdaError}</Box>
                )}
              </>
            )}
          />
        </Grid>
      </Grid>

      <Grid container sx={{ p: 2 }} spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField label="NDC Number" value={mappedValues.ndcNumber} fullWidth disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Drug Name" value={mappedValues.drugName} fullWidth disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Manufacturer" value={mappedValues.manufacturer} fullWidth disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Dosage Form" value={mappedValues.dosageForm} fullWidth disabled />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField label="Strength" value={mappedValues.strength} fullWidth disabled />
        </Grid>
      </Grid>

      {imageUrl && (
        <Box
          sx={{
            px: 2,
            pb: 2,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <img
            src={imageUrl}
            alt={mappedValues.drugName || "Wishlist item"}
            style={{
              maxWidth: "240px",
              maxHeight: "240px",
              width: "auto",
              height: "auto",
              borderRadius: "8px",
            }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "end", flexDirection: "row", m: 2, gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate(routeConfigs.wishlist.path)}>
          Cancel
        </Button>
        <Button
          size="large"
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          Add to wishlist
          {loading && <CircularProgress sx={{ ml: 2 }} size={14} color="inherit" />}
        </Button>
      </Box>
    </Paper>
  );
};

export default NewWishlistItem;
